<template>
  <div class="warranty-info">
    <Loader
      v-if="loaderEnabled"
      :logo="loaderLogo"
    ></Loader>
    <KTCodePreview v-bind:title="title">
      <template v-slot:toolbar>
        <b-button
          v-if="formModel.imeiCode"
          size="sm"
          variant="outline-primary"
          @click="onShowWarrantyImeiHistory"
        >
          <i class="fa fa-history mr-1"></i>
          Lịch sử bảo hành
        </b-button>
      </template>
      <template v-slot:preview>
        <LoadingSpinner
          v-if="isLoadingComponent"
          :message="'Đang tải dữ liệu phiếu tiếp nhận...'"
        />
        <template v-else>
          <b-row class="col-md-12 mb-6 m-0 p-0">
            <WarrantyCodeHeader
              :code="formModel.code"
              :created-at="formModel.createdAt"
              :bill-number="formModel.billNumber"
            />
          </b-row>
          <WarrantyReceptionSection
            v-model="formModel"
            :warranty-service-options="warrantyServiceOptions"
            :accompanying-equipments="accompanyingEquipments"
            :option-stores="optionStores"
            :inspected-by-employee-name="inspectedByEmployeeName"
            :is-edit-mode="isEditMode"
            :is-create-from-bill="isCreateFromBill"
            :allow-package-dismiss="isAllowPackageDismiss"
            :can-update-service-type="canUpdateWarrantyServiceType"
            @employee-selected="onInspectedByEmployeeSelected"
            @imei-selected="onImeiSelected"
          />
        </template>
      </template>
      <template v-slot:foot>
        <SaveButton
          v-if="(checkPermission('WARRANTY_RECEPTION_UPDATE') || checkPermission('WARRANTY_UPDATE_UNLIMIT')) && !isViewOnlyReturned"
          :is-busy="isLoading || isLoadingComponent"
          text="Lưu"
          variant="primary"
          size="sm"
          button-class="font-weight-bolder mr-4"
          @click="saveWarrantyReception"
        >
          <i class="fas fa-save mr-1"></i>
          Lưu
        </SaveButton>
        <b-button
          v-if="checkPermission('WARRANTY_RECEPTION_UPDATE') && !isEditMode"
          size="sm"
          variant="danger"
          class="font-weight-bolder mr-4"
          :disabled="isLoading"
          @click="openRejectionModal"
        >
          <i class="fas fa-times-circle mr-1"></i>
          Từ chối bảo hành
        </b-button>
        <b-button
          size="sm"
          variant="secondary"
          class="font-weight-bolder"
          @click="cancel"
        >
          <i class="fas fa-arrow-left mr-1"></i>
          Huỷ
        </b-button>
        <template>
          <b-modal
            ref="modal-warranty-imei-history"
            hide-footer
            title="Lịch sử bảo hành"
            id="modal-warranty-imei-history"
            size="xl"
          >
            <WarrantyImeiHistory :imeiCode="formModel.imeiCode" />
          </b-modal>
          <warranty-rejection-reason-modal
            ref="modal-rejection"
            v-model="formModel.rejectReason"
            :is-loading="isLoading"
            @submit="saveWarrantyReception('rejection')"
            @hidden="formModel.rejectReason = ''"
          />
        </template>
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue'
import { SET_BREADCRUMB } from '@/core/services/store//modules/breadcrumbs.module'
import { mapGetters } from 'vuex'
import Loader from '@/view/content/Loader.vue'
import timeUtils from '@/utils/date'
import {
  findOneReception,
  createReception,
  updateReception,
  createWarrantyRejection,
  getAccompanyingEquipments,
  getWarrantyServices,
  checkWarrantyByImei,
  getTradeInInfo,
} from './utils/warrantyApi'
import { handleError, currencyMask, unMaskPrice, makeToastFaile } from '@/utils/common'
import {
  formatDBDate,
  DATETIME_DB_FORMAT,
  CLIENT_DATE_TIME_NO_SECONDS,
  convertToDateSlash,
  formatSpecificDate,
  DATE_FORMAT,
} from '@/utils/date'
import permissionMixin from '@/view/mixins/permission.mixin'
import apiCommonMixin from '@/view/mixins/api-common.mixins'
import { PAGE_ACTION, WARRANTY_RECEPTION_STATUS } from '@/utils/enum'
import WarrantyReceptionSection from './components/WarrantyReceptionSection.vue'
import WarrantyCodeHeader from './components/WarrantyCodeHeader.vue'
import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
import WarrantyImeiHistory from './components/ImeiWarrantyHistory.vue'
import WarrantyRejectionReasonModal from './components/WarrantyRejectionReasonModal.vue'
import isNil from 'lodash/isNil'
import { getExternalNoteFromTradeIn } from './utils/listHelpers'
export default {
  mixins: [permissionMixin, apiCommonMixin],
  data() {
    return {
      isLoadingComponent: false,
      isLoading: false,
      formModel: {
        id: null,
        code: '',
        warrantyServiceId: null,
        customerId: null,
        customerName: '',
        storeId: null,
        storeName: '',
        customerPhone: '',
        customerContactNumber: '',
        customerAddress: '',
        customerEmail: '',
        productId: '',
        productName: '',
        imeiCode: '',
        purchaseDate: null,
        inspectedByEmployeeId: null,
        expectedReturnDate: null,
        estimatedCost: null,
        issueDescription: '',
        externalNote: '',
        internalNote: '',
        receiptNumber: '',
        trackingNumber: '',
        accompanyingItems: [],
        billNumber: null,
        warrantyProduct: {},
        warrantyProductId: null,
        rejectReason: '',
        status: null,
        returnCount: 0,
      },
      accompanyingEquipments: [],
      warrantyServiceOptions: [],
      inspectedByEmployeeName: '',
      optionStores: [],
      mask: currencyMask,
      dpConfigs: {
        date: timeUtils.DP_CONFIG.date,
        datetime: timeUtils.DP_CONFIG_DATETIME.date,
      },
    }
  },
  props: {
    warrantyId: {
      type: [Number, String],
      default: null,
    },
  },
  components: {
    KTCodePreview,
    Loader,
    WarrantyReceptionSection,
    WarrantyCodeHeader,
    LoadingSpinner,
    WarrantyImeiHistory,
    WarrantyRejectionReasonModal,
  },
  computed: {
    ...mapGetters(['breadcrumbs', 'pageTitle', 'layoutConfig']),
    loaderEnabled() {
      return !/false/.test(this.layoutConfig('loader.type'))
    },
    loaderLogo() {
      return process.env.BASE_URL + this.layoutConfig('loader.logo')
    },
    title() {
      return this.isEditMode ? 'Cập nhật phiếu tiếp nhận' : 'Tạo mới phiếu tiếp nhận'
    },
    isEditMode() {
      return !!this.$route.query.id || !!this.warrantyId
    },
    isCreateFromBill() {
      const { billNumber, action } = this.$route.query
      return !!(billNumber && action === PAGE_ACTION.CREATE)
    },
    isAllowPackageDismiss() {
      return this.formModel.status === WARRANTY_RECEPTION_STATUS.RECEIVING
    },
    isViewOnlyReturned() {
      return (
        this.$route.query.action === 'view' &&
        this.formModel.status === WARRANTY_RECEPTION_STATUS.RETURNED &&
        !this.checkPermission('WARRANTY_UPDATE_UNLIMIT')
      )
    },
    canUpdateWarrantyServiceType() {
      return (
        this.checkPermission('WARRANTY_UPDATE_SERVICE_TYPE') ||
        this.checkPermission('WARRANTY_UPDATE_UNLIMIT')
      )
    },
    ...mapGetters(['billWarrantyForCreate']),
  },
  watch: {
    warrantyId: {
      handler(id) {
        if (id) this.getWarrantyInfo()
      },
      immediate: false,
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [{ title: this.title }])
    this.fetchAccompanyingEquipments()
    this.fetchWarrantyServices()
    this.fetchStores()
  },
  created() {
    if (this.isEditMode) {
      this.getWarrantyInfo()
    }
    // Load data from bill nếu tạo từ bill
    const { billNumber, tradeInId, action } = this.$route.query
    if (billNumber && action === PAGE_ACTION.CREATE) {
      this.isLoadingComponent = true
      this.loadDataFromBill().finally(() => {
        this.isLoadingComponent = false
      })
    } else if (tradeInId && action === PAGE_ACTION.CREATE) {
      this.isLoadingComponent = true;
      console.log('load data from trdein ...')
      this.loadDataFromTradeIn(tradeInId).finally(() => {
        this.isLoadingComponent = false
      })
    }
  },
  methods: {
    onShowWarrantyImeiHistory() {
      this.$refs['modal-warranty-imei-history'].show()
    },
    openRejectionModal() {
      this.formModel.rejectReason = ''
      this.$refs['modal-rejection'].show()
    },
    closeRejectionModal() {
      this.$refs['modal-rejection'].hide()
    },
    async loadDataFromTradeIn(tradeInId) {
      const tradeInInfo = await getTradeInInfo(tradeInId)
      if (!tradeInInfo) return

      // Fill thông tin khách hàng
      this.formModel.customerId = tradeInInfo.customerId || null
      this.formModel.customerName = tradeInInfo.customerName || ''
      this.formModel.customerPhone = tradeInInfo.customerPhone || ''
      this.formModel.customerContactNumber = tradeInInfo.customerPhone || ''
      this.formModel.customerAddress = tradeInInfo.customerAddress || ''
      this.formModel.customerEmail = tradeInInfo.customerEmail || ''

      // Fill thông tin cửa hàng
      this.formModel.storeId = tradeInInfo.storeId || null
      this.formModel.storeName = tradeInInfo.storeName || ''

      // Fill thông tin sản phẩm
      this.formModel.productId = tradeInInfo.productId || ''
      this.formModel.productName = tradeInInfo.productName || ''
      this.formModel.imeiCode = tradeInInfo.productIMEI || ''
      this.formModel.internalNote = tradeInInfo.note || ''
      this.formModel.externalNote = getExternalNoteFromTradeIn(tradeInInfo.tradeInDetails)

      // Check gói bảo hành nếu có IMEI
      await this.checkAndUpdateWarrantyProduct(this.formModel.imeiCode)
    },
    async loadDataFromBill() {
      const billInfo = this.billWarrantyForCreate
      if (!billInfo || !billInfo.billItems || billInfo.billItems.length === 0) {
        return
      }

      // Lấy item đầu tiên từ billItems
      const firstItem = billInfo.billItems[0]

      // Lưu billNumber
      this.formModel.billNumber = billInfo.billNumber
      this.formModel.purchaseDate = formatSpecificDate(
        billInfo.createdAt,
        CLIENT_DATE_TIME_NO_SECONDS,
        DATE_FORMAT,
      )

      // Fill thông tin khách hàng
      this.formModel.customerId = billInfo.customerId || null
      this.formModel.customerName = billInfo.customerName || ''
      this.formModel.customerPhone = billInfo.customerMobile || ''
      this.formModel.customerContactNumber = billInfo.customerMobile || ''

      // Fill thông tin cửa hàng
      this.formModel.storeId = billInfo.storeId || null
      this.formModel.storeName = billInfo.storeName || ''

      // Fill thông tin sản phẩm
      this.formModel.productId = firstItem.productId || ''
      this.formModel.productName = firstItem.productName || ''
      const imeiCode = firstItem.imeiCode || firstItem.externalImeiNo || ''
      this.formModel.imeiCode = imeiCode

      // Check gói bảo hành nếu có IMEI
      await this.checkAndUpdateWarrantyProduct(imeiCode)
    },
    onInspectedByEmployeeSelected(employee) {
      if (employee) {
        this.inspectedByEmployeeName = employee.name
      } else {
        this.inspectedByEmployeeName = ''
      }
    },
    async onImeiSelected(productData) {
      if (!productData || !productData.imeiCode) return

      if (
        productData.latestBillCreatedAt &&
        !this.formModel.purchaseDate &&
        !this.isCreateFromBill
      ) {
        const purchaseDate = formatSpecificDate(
          productData.latestBillCreatedAt,
          CLIENT_DATE_TIME_NO_SECONDS,
          DATE_FORMAT,
        )
        this.formModel.purchaseDate = purchaseDate
      }

      if (this.isEditMode) return

      await this.checkAndUpdateWarrantyProduct(productData.imeiCode)
    },
    async checkAndUpdateWarrantyProduct(imeiCode) {
      if (!imeiCode) return
      try {
        const warrantyInfo = await checkWarrantyByImei(imeiCode)
        if (warrantyInfo) {
          this.$set(this.formModel, 'warrantyProduct', warrantyInfo)
          this.$set(this.formModel, 'warrantyProductId', warrantyInfo.id)
        } else {
          this.$set(this.formModel, 'warrantyProduct', {})
          this.$set(this.formModel, 'warrantyProductId', null)
        }
      } catch (error) {
        this.$set(this.formModel, 'warrantyProduct', {})
        this.$set(this.formModel, 'warrantyProductId', null)
      }
    },
    async fetchAccompanyingEquipments() {
      try {
        this.accompanyingEquipments = await getAccompanyingEquipments()
      } catch (error) {
        handleError(error)
      }
    },
    async fetchWarrantyServices() {
      try {
        const services = await getWarrantyServices()
        // Filter only active services
        this.warrantyServiceOptions = services.filter((service) => service.isActive === 1)
      } catch (error) {
        handleError(error)
      }
    },
    async fetchStores() {
      try {
        const { stores, currentStoreId, currentStoreName } = await this.fetchStoreByUser()
        this.optionStores = this.mapDataTreeSelect(stores, ['id', 'name', 'shortName'])
        // Auto-select if only one store
        if (currentStoreId && !this.isEditMode) {
          this.formModel.storeId = currentStoreId
          this.formModel.storeName = currentStoreName
        }
      } catch (error) {
        handleError(error)
      }
    },
    mapDataTreeSelect(items = [], keys = []) {
      if (!items || items.length === 0) return []
      const [idKey, nameKey, shortNameKey] = keys
      return items.map((item) => ({
        id: item[idKey],
        label: item[nameKey] || item[shortNameKey],
        shortName: item[shortNameKey] || item[nameKey],
      }))
    },
    async getWarrantyInfo() {
      const id = this.warrantyId || this.$route.query.id
      if (!id) return
      this.isLoadingComponent = true
      try {
        const result = await findOneReception(id)
        if (result) {
          const accompanyingItemIds = (result.accompanyingEquipments || []).map(
            ({ accompanyingEquipmentId }) => accompanyingEquipmentId,
          )
          this.formModel = {
            id: result.id,
            code: result.code,
            warrantyServiceId: result.warrantyServiceId,
            customerId: result.customerId,
            customerName: result.customerName,
            storeId: result.storeId,
            storeName: result.storeName,
            customerPhone: result.customerPhone,
            customerContactNumber: result.customerContactNumber,
            customerAddress: result.customerAddress,
            customerEmail: result.customerEmail,
            productId: result.productId,
            productName: result.productName,
            imeiCode: result.imeiCode,
            inspectedByEmployeeId: result.inspectedByEmployeeId,
            estimatedCost: result.estimatedCost,
            issueDescription: result.issueDescription,
            externalNote: result.externalNote,
            internalNote: result.internalNote,
            receiptNumber: result.receiptNumber,
            trackingNumber: result.trackingNumber,
            accompanyingItems: accompanyingItemIds,
            expectedReturnDate: convertToDateSlash(result.expectedReturnDate),
            purchaseDate: convertToDateSlash(result.purchaseDate, DATE_FORMAT),
            warrantyProduct: result.warrantyProduct || {},
            warrantyProductId: result.warrantyProductId || null,
            status: result.status ?? null,
            rejectReason: result.rejectReason || '',
            returnCount: result.returnCount,
            createdAt: convertToDateSlash(result.createdAt),
          }
          this.formModel.billNumber = result.billNumber || null
        }
      } catch (error) {
        handleError(error)
      } finally {
        this.isLoadingComponent = false
      }
    },
    async saveWarrantyReception(mode = 'reception') {
      if (this.isViewOnlyReturned) return
      this.isLoading = true
      try {
        const { isValid, errors } = this.validateForm(mode)
        if (!isValid) {
          errors.forEach((error) => {
            makeToastFaile(error)
          })
          return
        }
        const payload = {
          warrantyServiceId: this.formModel.warrantyServiceId,
          storeId: this.formModel.storeId,
          customerId: this.formModel.customerId,
          customerPhone: this.formModel.customerPhone,
          customerContactNumber: this.formModel.customerContactNumber,
          customerAddress: this.formModel.customerAddress,
          customerEmail: this.formModel.customerEmail,
          customerName: this.formModel.customerName?.trim(),
          productId: this.formModel.productId,
          productName: this.formModel.productName?.trim(),
          imeiCode: this.formModel.imeiCode?.trim(),
          purchaseDate: formatDBDate(this.formModel.purchaseDate),
          inspectedByEmployeeId: this.formModel.inspectedByEmployeeId,
          expectedReturnDate: formatDBDate(
            this.formModel.expectedReturnDate,
            CLIENT_DATE_TIME_NO_SECONDS,
            DATETIME_DB_FORMAT,
          ),
          estimatedCost:
            !isNil(this.formModel.estimatedCost) ? unMaskPrice(this.formModel.estimatedCost) : null,
          issueDescription: this.formModel.issueDescription,
          externalNote: this.formModel.externalNote,
          internalNote: this.formModel.internalNote,
          receiptNumber: this.formModel.receiptNumber,
          trackingNumber: this.formModel.trackingNumber,
          accompanyingEquipments: this.formModel.accompanyingItems.map(
            (accompanyingEquipmentId) => ({
              accompanyingEquipmentId,
            }),
          ),
        }

        if (this.formModel.warrantyProductId) {
          payload.warrantyProductId = this.formModel.warrantyProductId
        }

        // Thêm billNumber nếu tạo từ bill
        if (this.formModel.billNumber) {
          payload.billNumber = this.formModel.billNumber
        }

        // Thêm tradeInId nếu tạo từ phiếu thu cũ
        if (this.$route.query.tradeInId) {
          payload.tradeInId = parseInt(this.$route.query.tradeInId, 10)
        }

        if (mode === 'rejection') {
          payload.rejectReason = this.formModel.rejectReason
          await createWarrantyRejection(payload)
          this.$refs['modal-rejection'].hide()
          this.$router.push({ name: 'warranty-rejections' })
          return
        }
        if (this.isEditMode) {
          await updateReception(this.formModel.id, payload)
        } else {
          await createReception(payload)
        }

        this.$router.push({
          name: 'warranty-receptions',
        })
      } finally {
        this.isLoading = false
      }
    },
    validateForm(mode) {
      const errors = []
      if (!this.formModel.warrantyServiceId) {
        errors.push('Yêu cầu chọn dịch vụ bảo hành')
      }
      if (!this.formModel.customerPhone) {
        errors.push('Yêu cầu nhập SĐT khách hàng')
      }
      if (!this.formModel.customerName) {
        errors.push('Yêu cầu nhập tên khách hàng')
      }
      if (!this.formModel.productName) {
        errors.push('Yêu cầu nhập tên sản phẩm')
      }
      if (!this.formModel.imeiCode) {
        errors.push('Yêu cầu nhập IMEI')
      }
      if (!this.formModel.expectedReturnDate && mode === 'reception') {
        errors.push('Yêu cầu nhập ngày trả')
      }
      if (mode === 'rejection' && !(this.formModel.rejectReason || '').trim()) {
        errors.push('Vui lòng nhập lý do từ chối')
      }
      if (!this.formModel.productId) {
        errors.push('Yêu cầu chọn sản phẩm!')
      }
      return { isValid: !errors.length, errors }
    },
    resetForm() {
      this.formModel = {
        id: null,
        code: '',
        warrantyServiceId: null,
        customerId: null,
        customerName: '',
        storeId: null,
        storeName: '',
        customerPhone: '',
        customerContactNumber: '',
        customerAddress: '',
        customerEmail: '',
        productId: '',
        productName: '',
        imeiCode: '',
        purchaseDate: null,
        inspectedByEmployeeId: null,
        expectedReturnDate: null,
        estimatedCost: 0,
        issueDescription: '',
        externalNote: '',
        internalNote: '',
        receiptNumber: '',
        trackingNumber: '',
        accompanyingItems: [],
        billNumber: null,
        warrantyProduct: {},
        warrantyProductId: null,
        rejectReason: '',
        status: null,
        returnCount: 0,
      }
      this.inspectedByEmployeeName = ''
    },
    cancel() {
      this.$router.push({ name: 'warranty-receptions', query: this.returnQuery })
    },
  },
}
</script>

<style lang="scss" scoped>
.warranty-info {
  .bootstrap-datetimepicker-widget.dropdown-menu {
    width: 21em;
  }
}
</style>
